import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Loader2, Award, Download, BookOpenCheck } from "lucide-react";
import { api, apiError, API } from "../../lib/api";
import { downloadFile } from "../../lib/api";
import { ModuleCard } from "../../components/academia/ModuleCard";
import { Plan30 } from "../../components/academia/Plan30";
import { EvalFinal } from "../../components/academia/EvalFinal";

export default function Academia() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/academia");
      setData(d);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-yellow-400" size={30} /></div>;
  }

  const pct = data.progress_pct;
  const complete = pct >= 100;

  return (
    <div data-testid="academia-page" className="space-y-6 max-w-4xl">
      <div className="rounded-2xl border border-yellow-400/30 p-6 sm:p-8" style={{ background: "linear-gradient(135deg, #022C22, #064E3B)" }}>
        <div className="flex items-center gap-2 text-yellow-400">
          <GraduationCap size={20} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Academia · Tu inicio en el camino</span>
        </div>
        <h1 className="font-bold text-3xl sm:text-4xl text-zinc-50 mt-2">ABC del <span className="text-yellow-400">Multinivel</span></h1>
        <p className="text-sm text-emerald-100/80 mt-2">{data.intro.subtitle} · 10 módulos, 15 ejercicios y 30 días de práctica.</p>

        <div className="mt-4 rounded-xl bg-black/25 border border-white/10 p-4">
          <p className="text-xs font-bold text-yellow-300 uppercase tracking-wider mb-1">Objetivo</p>
          <p className="text-sm text-zinc-200">{data.intro.objetivo}</p>
          <p className="text-xs font-bold text-yellow-300 uppercase tracking-wider mt-3 mb-1">Cómo se usa (muy fácil)</p>
          <ol className="space-y-1">
            {data.intro.como_usar.map((c, i) => (
              <li key={c} className="text-sm text-zinc-300 flex gap-2">
                <span className="text-yellow-400 font-black">{i + 1}.</span> {c}
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-zinc-300 font-semibold">Tu progreso</span>
            <span data-testid="academia-progress-pct" className="text-yellow-300 font-black">{pct}%</span>
          </div>
          <div className="h-3 rounded-full bg-black/40 border border-white/10 overflow-hidden">
            <div data-testid="academia-progress-bar" className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-yellow-400 transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            {data.completed_exercises}/15 ejercicios · {data.completed_plan}/6 etapas del plan · evaluación {data.eval_done ? "✓" : "pendiente"}
          </p>
        </div>

        {complete && (
          <div data-testid="academia-complete-banner" className="mt-5 rounded-xl border border-yellow-400/60 bg-yellow-400/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <Award size={28} className="text-yellow-400 shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-yellow-300">¡Felicidades! Completaste el ABC del Multinivel</p>
              <p className="text-xs text-zinc-300">Descarga tu constancia con tu nombre y compártela con tu equipo.</p>
            </div>
            <button
              data-testid="academia-certificate-btn"
              onClick={() => downloadFile(`${API}/academia/constancia`, "constancia-abc-multinivel.pdf")}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black bg-yellow-400 text-emerald-950 hover:bg-yellow-300 shrink-0"
            >
              <Download size={14} /> Descargar constancia
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <BookOpenCheck size={18} className="text-yellow-400" />
        <h2 className="font-bold text-xl text-zinc-100">Los 10 módulos</h2>
      </div>
      <div className="space-y-3">
        {data.modules.map((m, i) => (
          <ModuleCard key={m.module_id} module={m} index={i} onSaved={setData} />
        ))}
      </div>

      <Plan30 steps={data.plan30} onSaved={setData} />
      <EvalFinal evalData={{ questions: data.eval_questions, eval_done: data.eval_done }} onSaved={setData} />

      <p className="text-[11px] text-zinc-500 pb-4">
        Material original de capacitación inspirado en principios generales de Eric Worre, Mark y Rene Yarnell y Don Failla. No se garantizan ventas ni ingresos.
      </p>
    </div>
  );
}
